/**
 * Faction currencies (GDD §6). Every player keeps money in their own
 * currency; the exchange and all prices in content are quoted in ГС. Once a
 * cycle the rate of each currency (ГС per unit) is recalculated:
 *
 *   K = k0 · P / M · (1 + w · (s − 1/N))
 *
 * P — output of the last cycles at market prices, ГС; M — money supply;
 * s — share of the currency in exchange turnover; N — players.
 * Starting values for tuning.
 */
export const CURRENCY = {
  /** k0: chosen so that a fresh capital's currency starts near 1 ГС. */
  rateScale: 1,
  /** w: how much trade share moves the rate. */
  tradeShareWeight: 0.5,
  /** Rate before the first cycle has any output to look at. */
  startRate: 1,
  /** Hard bounds, so zero output or huge emission never break the economy. */
  minRate: 0.1,
  maxRate: 10,
  /**
   * Money held by the population, per person. Together with everything
   * ever printed it makes the money supply M: spending moves money to the
   * population, so it does not shrink M; only printing grows it.
   */
  moneyPerPerson: 100,
  /** Cycles of output (P) and turnover (s) the rate looks back on (GDD §6: 5). */
  windowCycles: 5,
  /** Fee for converting between a player's currency and ГС on the exchange (GDD §6: 5%). */
  conversionFee: 0.05,
  /** Largest single emission, in the player's currency. */
  maxEmission: 5000,
} as const;

/**
 * Reserve currency (GDD §6, the dollar allusion). A currency becomes the
 * reserve after meeting all three conditions for `declareCycles` cycles in
 * a row and loses the status after `loseCycles` cycles under `loseShare`.
 * The gap between 40% and 25% keeps the status from flickering.
 */
export const RESERVE = {
  /** Share of exchange turnover needed to declare. */
  declareShare: 0.4,
  /** The largest output in the system, and at least this share of the total. */
  declareOutputShare: 0.3,
  /** Commerce center level needed to declare. */
  commerceLevel: 3,
  declareCycles: 5,
  /** Below this share of turnover the issuer starts losing the status. */
  loseShare: 0.25,
  loseCycles: 5,
  /** Issuer's cut of every other player's exchange deal (seigniorage), share of its value. */
  seigniorage: 0.02,
  /** Issuer's emission counts this much in its money supply: the rest is exported. */
  issuerInflationWeight: 0.5,
  /** Currency crisis on losing the status: the rate drops by this share... */
  crisisDrop: 0.3,
  /** ...and recovers evenly over this many cycles. */
  crisisRecoveryCycles: 10,
} as const;
